import { useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  InfoBar,
  isTouchDevice,
  Select,
  ServiceError,
  TextField,
} from "@shapr3d/frontend-lib";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useLinkPersonalAccount } from "../../hooks/useAccount";
import { Team } from "../../hooks/useAccountTypes";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { Modal } from "../../lib/modal";
import { ElementClickAction } from "../../lib/TrackingAdapter";
import { isEmailInvalid } from "../../utils/validationUtils";

import style from "./LinkPersonalAccountModal.module.css";

type Props = {
  open: boolean;
  onClose: () => void;
  currentEmail: string;
  salesTeams: Team[];
};

const emailErrorCodes = new Set([
  "SAME_DOMAIN_AS_SALES_ACCOUNT",
  "EMAIL_NOT_PERSONAL_DOMAIN",
  "INVALID_EMAIL_FORMAT",
  "PERSONAL_ACCOUNT_NOT_FOUND",
  "EMAIL_ALREADY_LINKED",
  "TARGET_HAS_ACTIVE_SUBSCRIPTION",
]);

export default function LinkPersonalAccountModal({
  open,
  onClose,
  currentEmail,
  salesTeams,
}: Props) {
  const { t, m } = useTranslation();
  const tracker = useTracker();
  const [selectedTeamId, setSelectedTeamId] = useState<string>(salesTeams[0]?.id ?? "");
  const [generalError, setGeneralError] = useState<string | null>(null);

  const linkMutation = useLinkPersonalAccount();
  const { reset: resetMutation } = linkMutation;

  const errorCodeToMessage: Record<string, string> = useMemo(
    () => ({
      USER_NOT_IN_SALES_TEAM: t(m.LinkedPersonalAccount.ErrorUserNotInSalesTeam),
      INVALID_TEAM_SELECTION: t(m.LinkedPersonalAccount.ErrorInvalidTeamSelection),
      PERSONAL_ACCOUNT_ALREADY_LINKED: t(m.LinkedPersonalAccount.ErrorPersonalAccountAlreadyLinked),
      EMAIL_ALREADY_LINKED: t(m.LinkedPersonalAccount.ErrorEmailAlreadyLinked),
      TARGET_HAS_ACTIVE_SUBSCRIPTION: t(m.LinkedPersonalAccount.ErrorTargetHasActiveSubscription),
      SAME_DOMAIN_AS_SALES_ACCOUNT: t(m.LinkedPersonalAccount.ErrorSameDomainAsSalesAccount),
      EMAIL_NOT_PERSONAL_DOMAIN: t(m.LinkedPersonalAccount.ErrorEmailNotPersonalDomain),
      INVALID_EMAIL_FORMAT: t(m.LinkedPersonalAccount.ErrorInvalidEmailFormat),
      PERSONAL_ACCOUNT_NOT_FOUND: t(m.LinkedPersonalAccount.ErrorPersonalAccountNotFound),
    }),
    [t, m]
  );

  const schema = useMemo(
    () =>
      z.object({
        personalEmail: z
          .string()
          .nonempty(t(m.LinkedPersonalAccount.ValidationEmailRequired))
          .refine(
            (value) => !isEmailInvalid(value),
            t(m.LinkedPersonalAccount.ValidationEmailInvalid)
          )
          .refine(
            (value) =>
              value.split("@")[1]?.toLowerCase() !== currentEmail.split("@")[1]?.toLowerCase(),
            t(m.LinkedPersonalAccount.ValidationSameDomain)
          ),
        confirmation: z
          .string()
          .refine(
            (value) => value === "CONFIRM",
            t(m.LinkedPersonalAccount.ValidationConfirmationRequired)
          ),
      }),
    [currentEmail, t, m]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    setValue,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      personalEmail: "",
      confirmation: "",
    },
  });

  useEffect(() => {
    if (open) {
      setGeneralError(null);
      reset({ personalEmail: "", confirmation: "" });
      resetMutation();
    }
  }, [open, reset, resetMutation]);

  useEffect(() => {
    if (open && selectedTeamId === "" && salesTeams.length > 0) {
      setSelectedTeamId(salesTeams[0].id);
    }
  }, [open, salesTeams, selectedTeamId]);

  const onSubmit = handleSubmit(async (data) => {
    setGeneralError(null);
    tracker.trackButtonClick({
      action: ElementClickAction.LINK_PERSONAL_ACCOUNT_CONFIRM,
    });

    try {
      await linkMutation.mutateAsync({
        personalEmail: data.personalEmail.toLowerCase(),
        teamId: selectedTeamId,
      });
      onClose();
    } catch (error) {
      const code = error instanceof ServiceError ? error.serviceErrorResponse.code : undefined;
      const errorMessage =
        errorCodeToMessage[code ?? ""] ?? t(m.LinkedPersonalAccount.ErrorGeneric);

      setValue("confirmation", "");

      if (code && emailErrorCodes.has(code)) {
        // Show email-related errors on the email field
        setError("personalEmail", { message: errorMessage });
      } else {
        // Show general errors as info bar
        setGeneralError(errorMessage);
      }
    }
  });

  if (generalError) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={t(m.LinkedPersonalAccount.ModalTitle)}
        size="small"
        telemetryName="link_personal_account"
      >
        <div className={style.form}>
          <InfoBar variant="critical" text={generalError} />
          <div className={style.buttonRow}>
            <Button onClick={onClose}>{t(m.LinkedPersonalAccount.CloseButton)}</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(m.LinkedPersonalAccount.ModalTitle)}
      size="small"
      telemetryName="link_personal_account"
    >
      <form className={style.form} onSubmit={onSubmit}>
        <p className={style.description}>{t(m.LinkedPersonalAccount.ModalDescription)}</p>

        {salesTeams.length > 1 && (
          <div className={style.teamSelectorSection}>
            <label className={style.selectorLabel}>
              {t(m.LinkedPersonalAccount.TeamSelectorLabel)}
            </label>
            <Select value={selectedTeamId} onChange={(e) => setSelectedTeamId(e.target.value)}>
              {salesTeams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </Select>
          </div>
        )}

        <TextField
          {...register("personalEmail")}
          placeholder={t(m.LinkedPersonalAccount.EmailPlaceholder)}
          errorMessage={errors.personalEmail?.message}
          autoFocus={!isTouchDevice()}
        />

        <div className={style.confirmationSection}>
          <p className={style.confirmationText}>
            {t(m.LinkedPersonalAccount.ConfirmationText).split("CONFIRM")[0]}
            <strong>CONFIRM</strong>
            {t(m.LinkedPersonalAccount.ConfirmationText).split("CONFIRM")[1]}
          </p>
          <TextField
            {...register("confirmation")}
            placeholder={t(m.LinkedPersonalAccount.ConfirmationPlaceholder)}
            errorMessage={errors.confirmation?.message}
          />
        </div>

        <div className={style.buttonRow}>
          <Button type="submit" loading={linkMutation.isPending}>
            {t(m.LinkedPersonalAccount.SubmitButton)}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
